import { useContext, useEffect, useRef, useState } from 'react';
import { NextPage } from 'next';
import { useRouter } from 'next/router';

import { Routes } from 'types/routes';

import { LIST_MARKUP } from 'consts/redux';
import { selectCmsPageSection } from 'utils/locale';

import { list as listCompanySites } from 'requests/companySite';

import LocaleContext from 'components/context/Locale';
import Head from 'components/layout/Head';
import SpinnerBlock from 'components/layout/SpinnerBlock';
import LayoutDefault from 'components/modules/public/layouts/Default';
import { ISearchParam, SearchParamCity, SearchParamPage, SearchParamTime, SearchParamValue, SearchParamValues, useSearchParams } from 'hooks/useSearchParams';

import Hero from './sections/Hero';
import Info from './sections/Info';
import ResultsList from './sections/ResultsList';
import StyledComponent from './styles';
import { Props, SearchOverrides, ValuesTypes } from './types';


const PublicPageCompanySites: NextPage<Props> = ({
    companies,
    jobCategories,
    cmsPage,
    initialSearchParams,
}) => {
    const router = useRouter();
    const localeContext = useContext(LocaleContext);
    const data = localeContext?.translations?.companySites;

    const resultsRef = useRef(null);


    const [searchState, setSearchState] = useState({
        companies,
        jobCategories,
        isLoaded: true,
        isLoading: false,
        firstLoad: true,
    });

    const { getSearchParams, getSearchParamsQueryValues, getSearchParamValue, setSearchParamValue, setSearchParamValueFromString } = useSearchParams({

        category: new SearchParamValues('jobCategorySlugs', initialSearchParams.jobCategorySlugs),
        search: new SearchParamValue('search', initialSearchParams.search),
        page: new SearchParamPage('page', initialSearchParams.page),
    });

    const searchParams = getSearchParams();

    /**
     * useEffect on router.query
     *
     */
    useEffect(() => {
        if (!searchState.firstLoad) {
            setSearchParamsFromQuery(router.query);
            search();
        }

        setSearchState((state) => ({ ...state, firstLoad: false }));
    }, [router.query]);


    /**
    * Set search params from query string
    *
    */
    const setSearchParamsFromQuery = (query: any = {}) => {
        Object.keys(searchParams).map((key) => {
            const value = query[searchParams[key].getQueryKey()];

            if (key == 'page') {
                setSearchParamValue(key, value);
                return;
            }

            setSearchParamValueFromString(key, value);
        });
    };

    /**
     * Update router query string
     *
     * @param attributes: any = {}
     */
    const updateQueryString = (searchParams: any, hash: string = '') => {
        const urlSearchParams = new URLSearchParams();

        Object.keys(searchParams)
            .map(key => {
                const value = searchParams[key].getQueryValue();
                if (value === '' || value === null || value === undefined || (Array.isArray(value) && value.length == 0)) return;
                urlSearchParams.set(searchParams[key].getQueryKey(), value);
            });


        router.push({
            pathname: Routes.PublicCompanySites,
            query: urlSearchParams.toString(),
            hash,
        }, undefined, { shallow: true });
    };


    /**
     * Search jobs
     *
     * @param number page = 1
     */
    const search = (page: number = 1) => {
        setSearchState(state => ({
            ...state,
            isLoading: true,
            isLoaded: false,
        }));

        listCompanySites({
            perPage: 9,
            ...getSearchParamsQueryValues(),
        })
            .then(listCollection => {
                setSearchState( state => ({
                    ...state,
                    isLoading: false,
                    isLoaded: true,
                    companies: {
                        ...LIST_MARKUP,
                        ...listCollection,
                    },
                }));
            })
            .catch(error => {
                console.error(error);

                setSearchState( state => ({
                    ...state,
                    ...LIST_MARKUP,
                    isLoading: false,
                    isLoaded: false,
                    error: true,
                }));
            });
    };

    const onSearch = (page: number = 1, hash: string = '') => {
        setSearchParamValue('page', page);
        updateQueryString({ ...getSearchParams() }, hash);
    };

    const handleChangeSearchParam = (key: string, value: any, clear?: boolean) => {
        setSearchParamValue(key, value, clear);
    };

    const handleChangeSearchParamAndSearch = (key: string, value: any, clear?: boolean) => {
        setSearchParamValue(key, value, clear);

        onSearch();
    };

    const handleChangeSearchParams = (searchParams: any) => {
        Object.keys(searchParams)
            .map(key => {
                setSearchParamValue(key, searchParams[key].getValue());
            });
        onSearch();
    };

    const handleChangePage = (page: number = 1) => {
        resultsRef.current.scrollIntoView();
        onSearch(page);
    };

    return (

        <StyledComponent className="public-page-company-sites">
            <Head
                {...data.meta}
                title={cmsPage?.metaTitle || data?.meta?.title}
                description={cmsPage?.metaDescription || data?.meta?.description}
                robots={cmsPage?.metaRobots}
                image={cmsPage?.image?.imageUrl}
            />
            <LayoutDefault>
                <Hero
                    translations={{
                        ...data.content.hero,
                        ...selectCmsPageSection(cmsPage, 'hero'),
                    }}
                    jobCategories={jobCategories}

                    searchParams={searchParams}
                    onSearch={handleChangeSearchParams}
                />

                <div ref={resultsRef} />

                <SpinnerBlock loading={searchState.isLoading} >

                    <ResultsList
                        translations={{
                            ...data.content.results,
                            ...selectCmsPageSection(cmsPage, 'results'),
                        }}
                        companies={searchState.companies}
                        onChangePage={handleChangePage}
                        searchParams={searchParams}
                    />
                    <Info
                        translations={{
                            ...data.content.info,
                            ...selectCmsPageSection(cmsPage, 'info'),
                        }}
                    />

                </SpinnerBlock>

            </LayoutDefault>
        </StyledComponent>
    );
};

export default PublicPageCompanySites;
